/**
 * attachmentChunkStore: manifest parser.
 *
 * The only place in this repo that turns manifest bytes into an
 * `AttachmentChunkManifest`. Both halves of the app call it — the version
 * callables in `functions/` and the reassembling reader in `react/` — so the
 * contract's refusal rule ("a reader MUST refuse a value it does not know;
 * refuse, never best-effort parse") is enforced once — including for
 * `contentMd5`, which this repo never reads and so can only judge here. Every
 * rejection throws an `Error` naming the violated field. The returned value is rebuilt from the
 * validated fields, never the raw parse, so nothing unvalidated escapes; the
 * informative `chunker` is mirrored when well-formed and dropped otherwise,
 * never defaulted.
 */
import {
  ATTACHMENT_MANIFEST_FORMAT_VERSION,
  MAX_ATTACHMENT_CHUNK_BYTES,
  type AttachmentChunkEntry,
  type AttachmentChunkManifest,
  type AttachmentChunkerParameters,
} from '../System.AttachmentChunkManifest.types';

const HEX_SHA256 = /^[0-9a-f]{64}$/;

/**
 * The base64 of a 16-byte md5: 22 base64 characters and the two pad characters a
 * 16-byte input always produces. Shape is the only check available here — the
 * bytes it digests are not — and it is worth making, because nothing in this
 * repo reads `contentMd5`. A writer emitting a placeholder would commit
 * successfully and fail every future download in `file-sync`, which verifies the
 * assembled whole against this value.
 */
const BASE64_MD5 = /^[A-Za-z0-9+/]{22}==$/;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJsonValue = (text: string): unknown => {
  try {
    const value: unknown = JSON.parse(text);
    return value;
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    throw new Error(`Attachment chunk manifest is not valid JSON: ${reason}`);
  }
};

const readChunker = (value: unknown): AttachmentChunkerParameters | undefined => {
  if (!isPlainObject(value)) return undefined;
  const { name, minBytes, avgBytes, maxBytes } = value;
  if (typeof name !== 'string') return undefined;
  if (
    typeof minBytes !== 'number' ||
    typeof avgBytes !== 'number' ||
    typeof maxBytes !== 'number'
  ) {
    return undefined;
  }
  return { name, minBytes, avgBytes, maxBytes };
};

const readChunkEntry = (value: unknown, index: number): AttachmentChunkEntry => {
  if (!isPlainObject(value)) {
    throw new Error(`Attachment chunk manifest chunk ${index} is not a JSON object`);
  }
  const { h, s, enc } = value;
  if (typeof h !== 'string' || !HEX_SHA256.test(h)) {
    throw new Error(
      `Attachment chunk manifest chunk ${index} digest h must be 64 lowercase hex characters, found ${JSON.stringify(h)}`,
    );
  }
  if (typeof s !== 'number' || !Number.isInteger(s) || s < 1 || s > MAX_ATTACHMENT_CHUNK_BYTES) {
    throw new Error(
      `Attachment chunk manifest chunk ${index} size s must be an integer from 1 to ${MAX_ATTACHMENT_CHUNK_BYTES}, found ${JSON.stringify(s)}`,
    );
  }
  if (enc === undefined) return { h, s };
  if (enc !== 'zstd') {
    throw new Error(
      `Attachment chunk manifest chunk ${index} encoding enc ${JSON.stringify(enc)} is not supported; formatVersion ${ATTACHMENT_MANIFEST_FORMAT_VERSION} allows only "zstd"`,
    );
  }
  return { h, s, enc };
};

export const parseAttachmentChunkManifest = (text: string): AttachmentChunkManifest => {
  const parsed = parseJsonValue(text);
  if (!isPlainObject(parsed)) {
    throw new Error('Attachment chunk manifest is not a JSON object');
  }
  const { formatVersion, hash, contentMd5, fileSize, chunks, chunker } = parsed;
  if (formatVersion !== ATTACHMENT_MANIFEST_FORMAT_VERSION) {
    throw new Error(
      `Attachment chunk manifest formatVersion ${JSON.stringify(formatVersion)} is not supported; this reader supports formatVersion ${ATTACHMENT_MANIFEST_FORMAT_VERSION}`,
    );
  }
  if (hash !== 'sha256') {
    throw new Error(
      `Attachment chunk manifest hash ${JSON.stringify(hash)} is not supported; formatVersion ${ATTACHMENT_MANIFEST_FORMAT_VERSION} requires "sha256"`,
    );
  }
  if (typeof contentMd5 !== 'string' || !BASE64_MD5.test(contentMd5)) {
    throw new Error(
      `Attachment chunk manifest contentMd5 must be a base64 md5 digest of 24 characters matching [A-Za-z0-9+/]{22}==, found ${JSON.stringify(contentMd5)}`,
    );
  }
  if (typeof fileSize !== 'number' || !Number.isSafeInteger(fileSize) || fileSize < 0) {
    throw new Error(
      `Attachment chunk manifest fileSize must be a non-negative integer, found ${JSON.stringify(fileSize)}`,
    );
  }
  if (!Array.isArray(chunks)) {
    throw new Error('Attachment chunk manifest chunks must be an array');
  }
  const rawChunks: unknown[] = chunks;
  const entries = rawChunks.map((chunk, index) => readChunkEntry(chunk, index));
  const chunkedSize = entries.reduce((total, entry) => total + entry.s, 0);
  if (chunkedSize !== fileSize) {
    throw new Error(
      `Attachment chunk manifest fileSize ${fileSize} does not equal the sum of its chunk sizes ${chunkedSize}`,
    );
  }
  const parameters = readChunker(chunker);
  return {
    formatVersion: ATTACHMENT_MANIFEST_FORMAT_VERSION,
    hash: 'sha256',
    fileSize,
    contentMd5,
    chunks: entries,
    ...(parameters === undefined ? {} : { chunker: parameters }),
  };
};
